/**
 * The connector directory shown on the landing page and in the docs.
 *
 * Two independent, non-overlapping claims are made per tool — never conflate them:
 *
 * 1. `native: true` — Dedupr has built a first-class, dedicated connector (schema
 *    discovery, health monitoring, the works). Per F01's MVP scope
 *    (docs/features/01-onboarding-connections/README.md), that's Salesforce, HubSpot
 *    and SharePoint only. This flag may ONLY ever mean "we built this."
 * 2. `oauth: true` — independent of anything Dedupr has built: the *vendor's own* API
 *    supports a genuine, self-serve OAuth 2.0 authorization flow (a customer creates
 *    their own app/client and clicks "authorize," rather than pasting a static API
 *    key) that Dedupr's generic REST connector can use today, with no approval or
 *    partner-program gate on Dedupr's side. Verified against each vendor's own
 *    developer documentation (2026-07-28 audit) — deliberately conservative: a tool
 *    is flagged `oauth: true` only when OAuth is usable *today*, by *any* customer,
 *    without Dedupr needing prior vendor approval. Tools where OAuth exists only via
 *    a formal partner/marketplace review (e.g. Gong, Apollo, ZoomInfo, QuickBooks
 *    production credentials, Google Drive's sensitive-scope verification, Notion's
 *    pre-review authorization URL), or only as a machine-to-machine client-credentials
 *    grant with no user consent screen (e.g. Marketo, Demandbase, Bombora, People.ai),
 *    are deliberately left unflagged — that gate is real and this page must not paper
 *    over it. Everything without either flag reaches Dedupr through the generic REST
 *    connector via a manually-generated API key, or a CSV/XLSX export.
 */

export interface ConnectorCategory {
  id: string;
  label: string;
  blurb: string;
  tools: Array<{ name: string; native?: boolean; oauth?: boolean }>;
}

export const CONNECTOR_CATEGORIES: ConnectorCategory[] = [
  {
    id: 'crm',
    label: 'CRM',
    blurb: 'Where the duplicate customer usually starts.',
    tools: [
      { name: 'Salesforce', native: true },
      { name: 'HubSpot', native: true },
      { name: 'Pipedrive', oauth: true },
      { name: 'Zoho CRM', oauth: true },
      { name: 'Microsoft Dynamics 365', oauth: true },
      { name: 'monday sales CRM', oauth: true },
      { name: 'Close', oauth: true },
      { name: 'Copper' },
      { name: 'Freshsales' },
      { name: 'Keap', oauth: true },
      { name: 'SugarCRM' },
      { name: 'Insightly' },
      { name: 'Nutshell' },
      { name: 'Salesflare' },
    ],
  },
  {
    id: 'engagement',
    label: 'Sales engagement & outbound',
    blurb: 'Sequences and dialers that create their own copies of a contact.',
    tools: [
      { name: 'Outreach' },
      { name: 'Salesloft', oauth: true },
      { name: 'Apollo.io' },
      { name: 'Groove' },
      { name: 'Reply.io' },
      { name: 'Instantly' },
      { name: 'Smartlead' },
      { name: 'Yesware' },
      { name: 'Mixmax' },
    ],
  },
  {
    id: 'enrichment',
    label: 'Data enrichment & intent',
    blurb: 'Every enrichment pass is a new opportunity to add a variant spelling.',
    tools: [
      { name: 'Lusha' },
      { name: 'ZoomInfo' },
      { name: 'Clearbit' },
      { name: 'Cognism' },
      { name: 'Clay' },
      { name: '6sense' },
      { name: 'Demandbase' },
      { name: 'Bombora' },
    ],
  },
  {
    id: 'revenue-intel',
    label: 'Revenue intelligence & forecasting',
    blurb: 'Rolls up pipeline from the CRM record — garbage in, garbage forecast out.',
    tools: [
      { name: 'Clari' },
      { name: 'Gong' },
      { name: 'Chorus' },
      { name: 'People.ai' },
      { name: 'Aviso' },
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing automation & email',
    blurb: 'Duplicate subscribers inflate the number you pay a vendor by.',
    tools: [
      { name: 'Marketo' },
      { name: 'Pardot', oauth: true },
      { name: 'ActiveCampaign' },
      { name: 'Klaviyo', oauth: true },
      { name: 'Mailchimp', oauth: true },
      { name: 'Braze' },
      { name: 'Iterable' },
      { name: 'Customer.io' },
    ],
  },
  {
    id: 'support',
    label: 'Support & customer success',
    blurb: 'Tickets and health scores attached to the wrong account.',
    tools: [
      { name: 'Zendesk', oauth: true },
      { name: 'Intercom', oauth: true },
      { name: 'Freshdesk' },
      { name: 'Help Scout', oauth: true },
      { name: 'Front', oauth: true },
      { name: 'Gainsight', oauth: true },
    ],
  },
  {
    id: 'finance',
    label: 'Finance & billing',
    blurb: 'The same company invoiced under three spellings.',
    tools: [
      { name: 'QuickBooks' },
      { name: 'Xero', oauth: true },
      { name: 'NetSuite', oauth: true },
      { name: 'Stripe' },
      { name: 'Chargebee' },
      { name: 'Sage' },
    ],
  },
  {
    id: 'docs-ops',
    label: 'Documents, storage & ops',
    blurb: 'Where the stale contract and the four price lists live.',
    tools: [
      { name: 'SharePoint', native: true },
      /* ⚠️ `native` flipped 2026-08-07 because the connector was BUILT, which is the only thing this
         flag may ever mean. Google Drive was previously unflagged on purpose — the header names
         "Google Drive's sensitive-scope verification" as a real gate — and that gate is still real:
         Google verifies the app, not just the credential, so until that completes it works only for
         named test users. `native` claims we built a dedicated connector, not that any customer can
         use it today; `connectable` in the runtime registry is what answers the second question, and
         this file is deliberately not wired to it. */
      { name: 'OneDrive', native: true },
      { name: 'Google Drive', native: true },
      { name: 'Box', oauth: true },
      { name: 'Dropbox', oauth: true },
      { name: 'Notion' },
      { name: 'Airtable', oauth: true },
      { name: 'monday.com', oauth: true },
      { name: 'Asana', oauth: true },
      { name: 'ClickUp', oauth: true },
    ],
  },
];
